import React, { createContext, useContext, useEffect, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { onAuthChange, getUserProfile } from "../services/auth-service";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../services/firebase";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Native Capacitor WebViews can be slower to initialize Firebase Auth
    const timeoutMs = typeof window !== "undefined" && Capacitor.isNativePlatform() ? 15000 : 8000;
    const timeout = setTimeout(() => setLoading(false), timeoutMs);

    const unsub = onAuthChange(async (firebaseUser) => {
      clearTimeout(timeout);
      try {
        if (firebaseUser) {
          setUser(firebaseUser);
          let prof = await getUserProfile(firebaseUser.uid);
          // If no Firestore profile yet, create it automatically
          if (!prof) {
            await setDoc(doc(db, "users", firebaseUser.uid), {
              displayName: firebaseUser.displayName || firebaseUser.email,
              email: firebaseUser.email,
              role: "student",
              language: "es",
              enrolledPrograms: [],
              createdAt: serverTimestamp(),
            });
            prof = await getUserProfile(firebaseUser.uid);
          }
          setProfile(prof);
        } else {
          setUser(null);
          setProfile(null);
        }
      } catch (err) {
        console.error("AuthProvider error:", err);
        setUser(firebaseUser ?? null);
        setProfile(null);
      } finally {
        setLoading(false);
      }
    });

    return () => {
      clearTimeout(timeout);
      unsub();
    };
  }, []);

  // Vuelve a leer el perfil después de que una función lo cambió (ej. joinVoiceTeam suma
  // el permiso de voz a una cuenta con sesión abierta).
  const refreshProfile = async () => {
    if (!user) return null;
    const prof = await getUserProfile(user.uid);
    setProfile(prof);
    return prof;
  };

  return (
    <AuthContext.Provider value={{ user, profile, loading, refreshProfile }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside AuthProvider");
  return {
    ...ctx,
    isAdmin:   ctx.profile?.role === "admin",
    isMentor:  ctx.profile?.role === "mentor" || ctx.profile?.role === "admin",
    isStudent: ctx.profile?.role === "student",
    // Permiso "Voz" (graba el audio del devocional). No es un rol; el admin graba cualquier idioma.
    isVoice: ctx.profile?.role === "admin" || Boolean(ctx.profile?.voice?.languages?.length),
    voiceLanguages: ctx.profile?.role === "admin" ? ["es", "en", "nah"] : ctx.profile?.voice?.languages || [],
  };
}
